import React, { useState } from "react";
import { ClipboardCheck, Layers3, RefreshCw, Loader2 } from "lucide-react";
import Select, { SingleValue } from "react-select";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { canAccessPath } from "../../../lib/screenAccess";
import { BordeiroKanbanItem } from "../../../services/bordeiroService";
import Modal from "../../../components/Modal";

interface BordeiroHeaderProps {
  dataReferencia: string;
  totalPendentes: number;
  totalFeitas: number;
  itens: BordeiroKanbanItem[];
  onRefresh: () => void;
  isRefreshing?: boolean;
  showLojaSelector?: boolean;
  lojaOptions?: Array<{ value: number; label: string }>;
  selectedLojaId?: number | undefined;
  onLojaChange?: (lojaId: number | undefined) => void;
}

const BordeiroHeader: React.FC<BordeiroHeaderProps> = ({
  dataReferencia,
  totalPendentes,
  totalFeitas,
  itens,
  onRefresh,
  isRefreshing = false,
  showLojaSelector = false,
  lojaOptions = [],
  selectedLojaId,
  onLojaChange,
}) => {
  const [selectedItem, setSelectedItem] = useState<BordeiroKanbanItem | null>(
    null
  );
  const { user } = useAuth();
  const canPainel = canAccessPath(user, "/painel-bordeiro");

  return (
    <>
      <div className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Layers3 size={20} className="text-primary" />
          <h1 className="text-lg font-bold text-neutral-800">
            Kanban de Bordas
          </h1>
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
            Dia {dataReferencia}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {showLojaSelector && (
            <div className="w-56">
              <Select
                inputId="bordeiro-loja-select"
                value={
                  selectedLojaId
                    ? (lojaOptions.find((o) => o.value === selectedLojaId) ??
                      null)
                    : null
                }
                onChange={(
                  opt: SingleValue<{ value: number; label: string }>
                ) => onLojaChange?.(opt?.value)}
                options={lojaOptions}
                isClearable={false}
                placeholder="Selecione a loja"
              />
            </div>
          )}
          {canPainel && (
            <Link
              to="/painel-bordeiro"
              className="flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-100"
            >
              <ClipboardCheck size={14} />
              Painel Bordeiro
            </Link>
          )}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isRefreshing ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Atualizando...
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Atualizar
              </>
            )}
          </button>
        </div>
      </div>

      <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-full bg-orange-100 px-3 py-1 font-semibold text-orange-700">
            Pendentes: {totalPendentes}
          </span>
          <span className="rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-700">
            Concluídas: {totalFeitas}
          </span>
        </div>
        {itens.length > 0 && (
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {itens.map((item) => (
              <button
                key={item.bordaNome}
                type="button"
                onClick={() => setSelectedItem(item)}
                className="flex items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition-colors hover:bg-neutral-50"
              >
                <p className="max-w-[180px] truncate font-semibold text-neutral-800">
                  {item.nome}
                </p>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="rounded bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                    {item.feitas}
                  </span>
                  <span className="rounded bg-orange-50 px-2 py-0.5 font-semibold text-orange-700">
                    {item.pendentes}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={
          selectedItem
            ? `Detalhes por tamanho - ${selectedItem.nome}`
            : "Detalhes por tamanho"
        }
      >
        {selectedItem && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-700">
                Concluídas: {selectedItem.feitas}
              </span>
              <span className="rounded-full bg-orange-100 px-3 py-1 font-semibold text-orange-700">
                Pendentes: {selectedItem.pendentes}
              </span>
            </div>

            {selectedItem.detalhesPorTamanho.length > 0 ? (
              <div className="space-y-2">
                {selectedItem.detalhesPorTamanho.map((detalhe) => (
                  <div
                    key={`${selectedItem.bordaNome}-${detalhe.tamanho}`}
                    className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2"
                  >
                    <p className="text-sm font-semibold text-neutral-800">
                      {detalhe.tamanho}
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="rounded bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                        Concluídas: {detalhe.feitas}
                      </span>
                      <span className="rounded bg-orange-50 px-2 py-0.5 font-semibold text-orange-700">
                        Pendentes: {detalhe.pendentes}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500">
                Sem detalhes por tamanho para esta borda.
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default BordeiroHeader;
